import { push } from 'react-router-redux'

import { actionTypes } from 'actions/actionTypes'
import { basketPostcodeChange } from 'actions/basket'
import loginActions from 'actions/login'
import { menuLoadBoxPrices } from 'actions/menu'
import { redirect } from 'actions/redirect'
import tempActions from 'actions/temp'
import { trackUTMAndPromoCode } from 'actions/tracking'
import {
  completeWizardPostcode,
  clickSeeThisWeeksMenu,
  signupSocialBelongingBanner,
  signupCheckAccountNewCustomer,
  signupCheckAccountExistingCustomer,
  signupApplyVoucher,
  deliveryInstructionDetailsClick,
  promoCodeInvalid,
  clickSeeThisWeeksMenuToCuratedBoxes,
} from 'actions/trackingKeys'
import { fetchCountByPostcode } from 'apis/signup'
import routes, { client } from 'config/routes'
import { isOptimizelyFeatureEnabledFactory } from 'containers/OptimizelyRollouts'
import { signupConfig } from 'routes/Signup/signupConfig'
import { stepByName } from 'routes/Signup/signupUtils'
import { getAccessToken } from 'selectors/auth'
import { getDeliveryDetailsInstructions } from 'selectors/deliveryDetails'
import { getUTMAndPromoCode } from 'selectors/tracking'
import logger from 'utils/logger'

export function signupStepsReceive(stepNames) {
  return {
    type: actionTypes.SIGNUP_STEPS_RECEIVE,
    stepNames,
  }
}

export function signupSetStep(step) {
  return (dispatch, getState) => {
    const signupState = getState().signup
    const stepNames = signupState.getIn(['wizard', 'stepNames'])

    if (step) {
      const newStepNumber = stepNames.findIndex((stepName) => stepName === step.get('name'))
      const isLastStep = newStepNumber === stepNames.size - 1

      dispatch({
        type: actionTypes.SIGNUP_STEP_SET,
        currentStepName: step.get('name'),
        currentStepNumber: newStepNumber,
        isLastStep,
        trackingData: {
          type: actionTypes.SIGNUP_STEP_SET,
          step: step.get('slug'),
          stepName: step.get('name'),
        },
      })
    }
  }
}

export function signupTracking() {
  return (dispatch, getState) => {
    const { basket } = getState()
    const postcode = basket.get('postcode')
    const numAdults = basket.get('numAdults')
    const numPortions = basket.get('numPortions')
    const date = basket.get('date')
    const slotId = basket.get('slotId')

    dispatch({
      type: actionTypes.SIGNUP_TRACKING,
      trackingData: {
        actionType: actionTypes.SIGNUP_TRACKING,
        postcode,
        numAdults,
        numPortions,
        date,
        slotId,
      },
    })
  }
}

/**
 * Redirects application to Signup exact step.
 */
export function signupNextStep(stepName) {
  const getIsSkipPropositionPageEnabled = isOptimizelyFeatureEnabledFactory(
    'beetroots_skip_sell_the_proposition_page_enabled',
  )

  return async (dispatch, getState) => {
    const isSkipPropositionPageEnabled = await getIsSkipPropositionPageEnabled(dispatch, getState)
    const step = stepByName(stepName)
    if (step) {
      const state = getState()
      const signupState = state.signup
      const isCurrentlyTheLastStep = signupState.getIn(['wizard', 'isLastStep'])
      const lastWizardStep = signupState.getIn(['wizard', 'stepNames']).last()
      const slug = step.get('slug')
      if (isCurrentlyTheLastStep && (step.get('name') === lastWizardStep || !slug)) {
        dispatch(signupTracking())

        if (isSkipPropositionPageEnabled) {
          return dispatch(signupGoToMenu())
        } else {
          const path = `${routes.client.signup}/${signupConfig.sellThePropositionPageSlug}`

          return dispatch(redirect(path))
        }
      }

      try {
        const { search } = getState().routing.locationBeforeTransitions
        dispatch(push(`${client.signup}/${slug}${search}`))
      } catch (e) {
        dispatch(push(`${client.signup}/${slug}`))
      }
    }

    return null
  }
}

export function signupGoToMenu() {
  return (dispatch) => {
    dispatch(trackUTMAndPromoCode(clickSeeThisWeeksMenu))
    dispatch(redirect(routes.client.menu))
    dispatch({ type: actionTypes.WIZARD_SEEN })
  }
}

export function signupGoToCuratedBoxes() {
  return (dispatch) => {
    dispatch(trackUTMAndPromoCode(clickSeeThisWeeksMenuToCuratedBoxes))
    dispatch(redirect('/menu/curated-boxes'))
    dispatch({ type: actionTypes.WIZARD_SEEN })
  }
}

export const trackSignupWizardAction =
  (type, additionalData = {}) =>
  (dispatch, getState) => {
    const { promoCode, UTM } = getUTMAndPromoCode(getState())

    dispatch({
      type,
      trackingData: {
        actionType: type,
        ...UTM,
        promoCode,
        ...additionalData,
      },
    })
  }

export const signupSetSocialBelongingOptions = (socialBelongingOptions) => ({
  type: actionTypes.SIGNUP_SET_SOCIAL_BELONGING_OPTIONS,
  ...socialBelongingOptions,
})

export const signupGetCountByPostcode = (postcode) => async (dispatch, getState) => {
  let socialBelongingOptions = {}
  try {
    const accessToken = getAccessToken(getState())
    const { data } = await fetchCountByPostcode(accessToken, { postcode })
    socialBelongingOptions = data
  } catch (e) {
    logger.error('No users found in current area')
  } finally {
    dispatch(signupSetSocialBelongingOptions(socialBelongingOptions))
  }
}

export function signupChangePostcode(postcode, nextStepName) {
  return async (dispatch, getState) => {
    await dispatch(basketPostcodeChange(postcode))

    if (!getState().error.get(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE, false)) {
      dispatch(trackSignupWizardAction(completeWizardPostcode, { postcode }))
      signupNextStep(nextStepName)(dispatch, getState)
    }
  }
}

export const signupChangeTempPostcode = (postcode) =>
  tempActions.temp('postcode', postcode.toUpperCase())

export const signupDismissDiscountAppliedBar = () => ({
  type: actionTypes.SIGNUP_DISMISS_DISCOUNT_APPLIED_BAR,
})

export const signupShowDiscountAppliedBar = () => ({
  type: actionTypes.SIGNUP_SHOW_DISCOUNT_APPLIED_BAR,
})

export const trackSocialBelongingBannerAppearance = () => (dispatch, getState) => {
  const state = getState()
  const { promoCode, UTM } = getUTMAndPromoCode(state)
  const district = state.signup.getIn(['wizard', 'district'])
  const amountOfCustomers = state.signup.getIn(['wizard', 'amountOfCustomers'])

  dispatch({
    type: signupSocialBelongingBanner,
    trackingData: {
      actionType: signupSocialBelongingBanner,
      ...UTM,
      promo_code: promoCode,
      district,
      number_of_customers: amountOfCustomers,
    },
  })
}

export const signupCheckAccountGoToBoxPrices = () => (dispatch) => {
  dispatch(menuLoadBoxPrices())
  dispatch(trackUTMAndPromoCode(signupCheckAccountNewCustomer))
  dispatch(redirect(`${routes.client.signup}/box-size`))
}

export const signupCheckAccountLogin = () => (dispatch) => {
  dispatch(trackUTMAndPromoCode(signupCheckAccountExistingCustomer))

  const { loginVisibilityChange } = loginActions
  dispatch(loginVisibilityChange(true))
}

export const signupApplyVoucherGoToDeliveries = () => (dispatch) => {
  dispatch(trackUTMAndPromoCode(signupApplyVoucher))
  dispatch(redirect(`${routes.client.myDeliveries}`))
}

export const trackDeliveryInstructionSelection = () => (dispatch, getState) => {
  const deliveryInstruction = getDeliveryDetailsInstructions(getState())

  dispatch({
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: deliveryInstructionDetailsClick,
      selection: deliveryInstruction,
    },
  })
}

export const trackingPromoCodeInvalid = (error) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: promoCodeInvalid,
    error,
  },
})
